/**
 * Routes source clicks by type.
 *
 * External sources → new browser tab
 * Platform entities → in-app navigation (with ?moo=1 to keep chat open)
 * PDF → callback to open PDF viewer panel
 * Overlay → callback to open overlay panels
 */

import type { PolicyMetadata, Source, SourceType } from '../../types';
import { getKBDocumentBlob } from '../../api/kb.api';
import { isInternalApiUrl } from '../../utils/authenticated-blob';
import { isFilePreviewSourceType } from '../../utils/file-preview-kind';

/**
 * Query param carrying a non-routable policy (P100/P250) deep link. Value is
 * ``<productType>:<policyId>``. Ask Moo reads it on load and re-opens the
 * policy drawer, giving "Copy link" parity with the "View policy" action for
 * products that have no full-page detail route (see ROUTABLE_POLICY_PRODUCT_TYPES).
 */
export const MOO_POLICY_PARAM = 'mooPolicy';

const EXTERNAL_TYPES: SourceType[] = ['url', 'jira', 'slack', 'confluence'];

/** Entity types that support in-app navigation. */
const NAVIGABLE_ENTITIES = new Set([
  'quote',
  'policy',
  'claim',
  'submission',
  'binder',
  'uw_entry',
  'account',
  'agency',
  'diary_entry',
]);

/** Append ``moo=1`` to a path, respecting any existing query string. */
function appendMooParam(path: string): string {
  return path.includes('?') ? `${path}&moo=1` : `${path}?moo=1`;
}

/** Roles that see quote citations routed to the UW inbox instead of detail. */
const UW_ROLES = ['UNDERWRITER', 'SUPER_ADMIN'];

/**
 * Products whose quotes have no "underwrite" tab in the ADMIN Underwriter
 * Workspace (see useUwRoutes: `supportsUnderwrite = product !==
 * ProductTypes.p100`). Routing a UW role to /admin/inbox/{id}/P100/underwrite
 * renders an empty workspace, so these products use the quote detail route.
 *
 * Admin-only: role-mapping.ts maps CARRIER/CLUSTER/POOL/PREMIUM_PROCESSOR/
 * RE_INSURER personas (and the default) to UNDERWRITER too, but their inbox
 * workspaces underwrite every product (e.g. CarrierUnderwriterWorkspace) and
 * their route tables have no /quote/detail — redirecting them would swap a
 * working route for a dead one.
 */
const NON_UNDERWRITE_QUOTE_PRODUCTS = new Set(['P100']);

/**
 * In-app quote path: UW inbox for UW roles, quote detail otherwise. Returns
 * null for the customer persona — policyholders have no ``/customer/quote/detail``
 * route, so their quote citations fall back to the Ask Moo quote drawer
 * (onShowQuote), the same pattern non-routable policies use with onShowPolicy.
 */
function buildQuotePath(
  id: string,
  productType: string,
  personaPrefix: string,
  userRole: string
): string | null {
  if (personaPrefix === '/customer') return null;

  const p100DetailRedirect =
    personaPrefix === '/admin' &&
    NON_UNDERWRITE_QUOTE_PRODUCTS.has(productType);
  const useUwInbox = UW_ROLES.includes(userRole) && !p100DetailRedirect;
  if (useUwInbox) {
    return productType
      ? `${personaPrefix}/inbox/${id}/${productType}/underwrite`
      : `${personaPrefix}/inbox/${id}`;
  }
  return productType
    ? `${personaPrefix}/quote/detail/${id}/${productType}/details`
    : `${personaPrefix}/quote/detail/${id}`;
}

/** Personas that put productType before policyId in the policy detail route. */
const PRODUCT_FIRST_PERSONAS = new Set(['/admin', '/carrier']);

/**
 * Policy productTypes that have a full-page detail route
 * (/persona/policy/detail/...). P100 and P250 use inline table row expansion
 * and have no routable detail page — their citations fall back to the overlay
 * drawer. Legacy adhoc excess (P_ONE_PLUS, PRIME_TECH_PLUS, …) is also
 * non-routable; PrimeX excess (PRIME_ONE_PLUS_US) uses the PX detail page.
 */
const ROUTABLE_POLICY_PRODUCT_TYPES = new Set([
  'P100_PRO',
  'P_ONE',
  'PRIME_TECH',
  'PRIME_ONE_TECH',
  'ZSP',
  'PRIME_ONE_AU',
  'PRIME_ONE_US',
  'PRIME_ONE_PLUS_US',
]);

function getActivePersonaPrefix(): string {
  const firstSegment = window.location.pathname.split('/')[1] || '';
  const knownPersonas = [
    'admin',
    'agency',
    'carrier',
    'cowbell',
    'customer',
    'da',
    'mssp',
    'pool',
    'premiumprocessor',
    'reinsurer',
  ];
  if (knownPersonas.includes(firstSegment)) return `/${firstSegment}`;
  return '/admin';
}

/**
 * In-app path to a generated quote, with the Moo panel kept open. Role-aware,
 * exactly like a quote citation (see buildEntityPath's `quote` case): UW roles go
 * to the underwriter inbox/review workspace; everyone else goes to the quote detail
 * page (the inbox workspace route only exists for admin/carrier personas). Used by
 * the copy-options card's link after a quote is generated.
 */
export function buildQuoteReviewPath(
  quoteId: string,
  productType: string,
  userRole = ''
): string {
  const persona = getActivePersonaPrefix();
  // buildQuotePath is null only for the customer persona, which never reaches
  // the quote-draft / UW cards that call this; fall back to the persona
  // dashboard to keep a valid route rather than pushing null.
  const path = buildQuotePath(quoteId, productType, persona, userRole);
  return appendMooParam(path ?? `${persona}/dashboard`);
}

export interface SourcePreviewCallbacks {
  onOpenPdf?: (source: Source) => void;
  onOpenFilePreview?: (source: Source) => void;
  onShowComparables?: (source: Source) => void;
  onShowThreatActor?: (source: Source) => void;
  onShowCve?: (source: Source) => void;
  onShowFindings?: (source: Source) => void;
  onShowEmail?: (source: Source) => void;
  onShowPolicy?: (source: Source) => void;
  onShowQuote?: (source: Source) => void;
  navigate?: (path: string) => void;
  userRole?: string;
}

export function isExternalSource(type: SourceType): boolean {
  return EXTERNAL_TYPES.includes(type);
}

export function isPlatformObject(type: SourceType): boolean {
  return NAVIGABLE_ENTITIES.has(type);
}

/**
 * Resolve the identifier used to build an entity's in-app route.
 *
 * `objectId` is the entity's routable id. The `*Number` fields are human-facing
 * business identifiers; only `policyNumber` doubles as a routable id (the policy
 * detail route resolves by number). Quote / uw_entry routes resolve by the quote
 * UUID (``useQuoteDetails({ id })`` → ``/api/quote/v1/:product/:quoteId``), so
 * ``quoteNumber`` is deliberately NOT a fallback here — routing on it yields a
 * broken ``/inbox/QCB-.../underwrite`` link. When ``objectId`` is absent the
 * caller falls back to ``source.url``.
 */
function resolveRoutingId(meta: Record<string, unknown>): unknown {
  return (
    meta.objectId ||
    meta.claimNumber ||
    meta.policyNumber ||
    meta.submissionNumber ||
    meta.binderNumber ||
    meta.accountId
  );
}

/**
 * Build the in-app path for a given entity source.
 *
 * URL patterns (from console route config):
 *   quote  (UW)   → /{persona}/inbox/{id}/{productType}/underwrite
 *   quote  (other) → /{persona}/quote/detail/{id}/{productType}/details
 *   policy         → /{persona}/policy/detail/{productType}/{id}/details
 *   claim          → /{persona}/claims/detail/{id}/summary
 *   submission     → /{persona}/submissions/summary/{id}/summary
 *   account        → /{persona}/account/{id}
 *   agency         → /{persona}/agencies/details/{id}/profile
 *   binder         → /{persona}/policy/detail/{productType}/{id}/details
 *   uw_entry       → /{persona}/inbox/{id}/{productType}/underwrite
 */
function buildEntityPath(
  source: Source,
  objectId: string | unknown,
  meta: Record<string, unknown>,
  personaPrefix: string,
  userRole: string
): string | null {
  const productType = (meta.productType as string) || '';
  const id = String(objectId);

  switch (source.type) {
    case 'quote':
      return buildQuotePath(id, productType, personaPrefix, userRole);

    case 'policy': {
      // Policyholders have no `/customer/policy/detail` route (only the
      // `/customer/policies` list), so a routable productType would otherwise
      // build a dead URL that 404s. Return null for the customer persona to
      // fall back to the Ask Moo policy drawer (onShowPolicy) — the same guard
      // buildQuotePath applies to customer quote citations.
      if (personaPrefix === '/customer') return null;
      if (!productType || !ROUTABLE_POLICY_PRODUCT_TYPES.has(productType))
        return null;
      return PRODUCT_FIRST_PERSONAS.has(personaPrefix)
        ? `${personaPrefix}/policy/detail/${productType}/${id}/details`
        : `${personaPrefix}/policy/detail/${id}/${productType}/details`;
    }

    case 'binder': {
      if (!productType) return null;
      return `${personaPrefix}/quote/detail/${id}/${productType}/details`;
    }

    case 'claim':
      return `${personaPrefix}/claims/detail/${id}/summary`;

    case 'diary_entry': {
      // Citation deep-link: open the parent claim's Diary tab and scroll
      // to / highlight the entry via ``?diaryEntryId=`` (see ClaimDiary).
      const claimId = (meta.claimId as string) || id;
      const diaryEntryId = (meta.diaryEntryId as string) || id;
      return `${personaPrefix}/claims/detail/${claimId}/diary?diaryEntryId=${diaryEntryId}`;
    }

    case 'submission':
      return `${personaPrefix}/submissions/summary/${id}/summary`;

    case 'account':
      return `${personaPrefix}/account/${id}`;

    case 'agency':
      return `${personaPrefix}/agencies/details/${id}/profile`;

    case 'uw_entry':
      return productType
        ? `${personaPrefix}/inbox/${id}/${productType}/underwrite`
        : `${personaPrefix}/inbox/${id}`;

    default:
      return `${personaPrefix}/${source.type}/${id}`;
  }
}

/** Universal fallback (OMNI-697): fetch an internal API URL as an
 *  authenticated blob and open it in a new tab — a bare `window.open` on an
 *  internal `/api/...` URL has no auth header attached and would 401.
 *
 *  `window.open` is called synchronously, before the fetch, and navigated
 *  later via `.location` (PR feedback on OMNI-697): calling `window.open`
 *  only after an awaited fetch resolves falls outside the click's
 *  user-gesture window, so every major browser treats it as a popup and
 *  blocks it — silently reintroducing the exact dead-click this fallback
 *  exists to fix. Opening a blank tab immediately and pointing it at the
 *  blob URL once it's ready keeps the open() call itself inside the
 *  gesture. This means (unlike the external-URL branches in this file)
 *  `noopener`/`noreferrer` can't be used here — they suppress the window
 *  reference this needs to navigate later — but the tab initially loads
 *  about:blank and only ever receives our own fetched blob content, not an
 *  arbitrary external URL, so the reverse-tabnabbing risk those flags guard
 *  against doesn't apply the same way; nulling `opener` immediately is a
 *  cheap extra precaution. */
function openInternalUrlAsBlob(url: string): void {
  const newTab = window.open();
  if (newTab) newTab.opener = null;

  getKBDocumentBlob(url)
    .then((response) => {
      const objectUrl = URL.createObjectURL(response.data);
      if (newTab) {
        newTab.location.href = objectUrl;
      }
      // Revoke on a delay rather than immediately: the new tab's navigation
      // reads the blob URL asynchronously, so revoking right after this
      // call returns could race the browser and break the load. 60s is
      // generous for any real page load while still freeing memory.
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
    })
    .catch((err) => {
      console.error('Failed to open document', err);
      newTab?.close();
    });
}

export function handleSourceClick(
  source: Source,
  callbacks?: SourcePreviewCallbacks
): void {
  if (EXTERNAL_TYPES.includes(source.type) && source.url) {
    window.open(source.url, '_blank', 'noopener,noreferrer');
    return;
  }

  if (NAVIGABLE_ENTITIES.has(source.type) && source.metadata) {
    const meta = source.metadata as unknown as Record<string, unknown>;
    const objectId = resolveRoutingId(meta);

    if (objectId) {
      const personaPrefix = getActivePersonaPrefix();
      const userRole = callbacks?.userRole || '';
      const path = buildEntityPath(
        source,
        objectId,
        meta,
        personaPrefix,
        userRole
      );

      if (path === null) {
        const productType = (meta.productType as string) || '';
        // Non-routable policy products (P100/P250) have a known productType —
        // open in the policy drawer. Missing productType or non-policy nulls
        // (e.g. binder without productType) fall back to source.url.
        if (
          source.type === 'policy' &&
          productType &&
          callbacks?.onShowPolicy
        ) {
          callbacks.onShowPolicy(source);
        } else if (source.type === 'quote' && callbacks?.onShowQuote) {
          // Customer persona has no quote detail route — open the quote drawer.
          callbacks.onShowQuote(source);
        } else if (source.url) {
          window.open(source.url, '_blank', 'noopener,noreferrer');
        }
        return;
      }

      const url = appendMooParam(path);
      if (callbacks?.navigate) {
        callbacks.navigate(url);
      } else {
        window.location.href = url;
      }
    } else if (source.url) {
      window.open(source.url, '_blank', 'noopener,noreferrer');
    }
    return;
  }

  if (source.type === 'pdf') {
    if (callbacks?.onOpenPdf) {
      callbacks.onOpenPdf(source);
    } else if (source.url) {
      window.open(source.url, '_blank', 'noopener,noreferrer');
    }
    return;
  }

  // These five entity types each own a dedicated in-app panel with an
  // established "no callback wired → do nothing, no stray navigation"
  // contract (predates OMNI-697; threat_actor/cve have their own tests
  // asserting exactly this). Each branch returns unconditionally once its
  // type matches, so none of them ever reaches the universal fallback below
  // — that fallback is for types with no dedicated handling at all, not for
  // these simply-unwired-in-this-host-integration cases.
  if (source.type === 'comparables') {
    if (callbacks?.onShowComparables) callbacks.onShowComparables(source);
    return;
  }

  if (source.type === 'threat_actor') {
    if (callbacks?.onShowThreatActor) callbacks.onShowThreatActor(source);
    return;
  }

  if (source.type === 'cve') {
    if (callbacks?.onShowCve) callbacks.onShowCve(source);
    return;
  }

  if (source.type === 'findings') {
    if (callbacks?.onShowFindings) callbacks.onShowFindings(source);
    return;
  }

  if (source.type === 'email') {
    if (callbacks?.onShowEmail) callbacks.onShowEmail(source);
    return;
  }

  if (isFilePreviewSourceType(source.type) && callbacks?.onOpenFilePreview) {
    callbacks.onOpenFilePreview(source);
    return;
  }

  // Universal fallback (OMNI-697): any type reaching this point — an
  // unwired spreadsheet/document/image, or any future/unrecognised type —
  // degrades to opening source.url rather than doing nothing.
  if (!source.url) return;
  if (isInternalApiUrl(source.url)) {
    openInternalUrlAsBlob(source.url);
  } else {
    window.open(source.url, '_blank', 'noopener,noreferrer');
  }
}

/** Human-readable action label for the primary tooltip button. */
export function getPreviewActionLabel(sourceType: SourceType): string {
  switch (sourceType) {
    case 'quote':
      return 'View quote';
    case 'policy':
      return 'View policy';
    case 'claim':
      return 'View claim';
    case 'submission':
      return 'View submission';
    case 'binder':
      return 'View binder';
    case 'uw_entry':
      return 'Open UW Review';
    case 'account':
      return 'View account';
    case 'agency':
      return 'View agency';
    case 'pdf':
    case 'spreadsheet':
    case 'document':
    case 'image':
      return 'Open preview';
    case 'jira':
      return 'Open in Jira';
    case 'slack':
      return 'Open in Slack';
    case 'confluence':
      return 'Open in Confluence';
    case 'email':
      return 'Open email';
    case 'diary_entry':
      return 'View diary entry';
    case 'url':
      return 'Open link';
    default:
      return 'Open preview';
  }
}

/** Build the full URL for a source (for clipboard copy). */
export function buildSourceUrl(source: Source, userRole = ''): string {
  if (EXTERNAL_TYPES.includes(source.type) && source.url) {
    return source.url;
  }

  if (NAVIGABLE_ENTITIES.has(source.type) && source.metadata) {
    const meta = source.metadata as unknown as Record<string, unknown>;
    const objectId = resolveRoutingId(meta);

    if (objectId) {
      const personaPrefix = getActivePersonaPrefix();
      const path = buildEntityPath(
        source,
        objectId,
        meta,
        personaPrefix,
        userRole
      );
      if (path !== null) {
        return `${window.location.origin}${path}`;
      }
      // Non-routable policy (P100/P250): no full-page detail route, but the
      // policy drawer is reachable via a deep link Ask Moo re-opens on load.
      // Parity with handleSourceClick's onShowPolicy fallback above.
      const productType = (meta.productType as string) || '';
      if (source.type === 'policy' && productType) {
        return `${window.location.origin}${buildPolicyDrawerPath(
          personaPrefix,
          productType,
          String(objectId)
        )}`;
      }
    }
  }

  return source.url || '';
}

/**
 * Persona dashboard route carrying the drawer deep-link param. The policy
 * drawer is an overlay that floats over any page, so the landing route only
 * needs to be one that always exists, is authorized, and does not redirect
 * (a cold hard-load of ``/{persona}/policies`` bounces to the dashboard and
 * drops the query string, losing the param before Ask Moo can read it). The
 * dashboard is the safe canonical landing for every persona.
 */
function buildPolicyDrawerPath(
  personaPrefix: string,
  productType: string,
  policyId: string
): string {
  const value = encodeURIComponent(`${productType}:${policyId}`);
  return appendMooParam(
    `${personaPrefix}/dashboard?${MOO_POLICY_PARAM}=${value}`
  );
}

/**
 * Persona-relative policies-list route for the active persona. Used to move a
 * deep-linked policy drawer onto the policies page (client-side, once scopes
 * have loaded) instead of leaving it on the cold-load landing dashboard.
 */
export function buildPoliciesListPath(): string {
  return `${getActivePersonaPrefix()}/policies`;
}

/**
 * Remove a lingering policy deep-link param from the URL (e.g. after the drawer
 * it opened is closed) so a later refresh / back-nav doesn't resurface it.
 * No-op when the param isn't present. Shared by the V1 and V2 integrations.
 */
export function stripMooPolicyParam(history: {
  location?: { search?: string; pathname?: string };
  replace: (loc: { pathname: string; search: string }) => void;
}): void {
  const search = history.location?.search ?? '';
  if (!search.includes(MOO_POLICY_PARAM)) return;
  const params = new URLSearchParams(search);
  params.delete(MOO_POLICY_PARAM);
  history.replace({
    pathname: history.location?.pathname ?? '',
    search: params.toString(),
  });
}

/**
 * Parse the ``mooPolicy`` deep-link param (``<productType>:<policyId>``) from a
 * location search string. Returns null when absent or malformed.
 */
export interface MooPolicyDeepLink {
  productType: string;
  policyId: string;
}

export function parseMooPolicyParam(search: string): MooPolicyDeepLink | null {
  const value = new URLSearchParams(search).get(MOO_POLICY_PARAM);
  if (!value) return null;
  const sep = value.indexOf(':');
  if (sep <= 0 || sep === value.length - 1) return null;
  return {
    productType: value.slice(0, sep),
    policyId: value.slice(sep + 1),
  };
}

/**
 * Build a minimal policy Source from a parsed ``mooPolicy`` deep link. The
 * policy drawer self-fetches full details from ``objectId`` + ``productType``
 * (see PolicyOverlayDrawer), so the human-facing fields can be left empty.
 */
export function buildMooPolicySource(parsed: MooPolicyDeepLink): Source {
  const metadata: PolicyMetadata = {
    objectId: parsed.policyId,
    policyNumber: parsed.policyId,
    accountName: '',
    effectiveDate: '',
    expirationDate: '',
    productType: parsed.productType,
  };
  return {
    id: parsed.policyId,
    citationIndex: 0,
    type: 'policy',
    title: '',
    description: '',
    url: null,
    metadata,
  };
}

/** Copy a source's link to clipboard. Returns true on success. */
export async function copySourceLink(
  source: Source,
  userRole = ''
): Promise<boolean> {
  const url = buildSourceUrl(source, userRole);
  if (!url) return false;
  try {
    await navigator.clipboard.writeText(url);
    return true;
  } catch {
    return false;
  }
}
